/**
 * The More menu: the overflow verbs live here, never in a footer.
 *
 * The trigger stays where it is drawn; the menu prints on the body, fixed to
 * the trigger, because the rail and the sheet both scroll and blur, and either
 * would clip it or turn a fixed child into a scrolling one. A click outside
 * closes it, Escape closes it and gives the trigger back the keyboard, and a
 * click on a menu button closes it unless the button sits inside an element
 * marked `data-keep-open` (the appearance picker).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./primitives.js";

export interface MoreMenuTriggerProps {
  open: boolean;
  toggle: () => void;
}

export interface MoreMenuProps {
  /** Draws the trigger; call `toggle` from its click. */
  trigger: (props: MoreMenuTriggerProps) => ReactNode;
  children: ReactNode;
  /** Which trigger edge the menu lines up with. */
  align?: "left" | "right";
  /** `up` opens above the trigger (the account menu at the foot of the rail). */
  direction?: "down" | "up";
  /** Extra classes on the `.more` wrapper and the printed menu's wrapper. */
  className?: string;
  /** The menu's accessible name. */
  label?: string;
}

export function MoreMenu({ trigger, children, align = "right", direction = "down", className, label }: MoreMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) wrapRef.current?.querySelector<HTMLElement>("button")?.focus();
  }, []);
  const toggle = useCallback(() => setOpen((was) => !was), []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const vertical =
      direction === "up" ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 };
    const horizontal = align === "left" ? { left: rect.left } : { right: window.innerWidth - rect.right };
    setPos({ ...vertical, ...horizontal });
  }, [open, align, direction]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The menu is the innermost thing open: Escape is its, not the sheet's.
      event.stopPropagation();
      close(true);
    };
    const onResize = () => close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  const onMenuClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest("button, a")) return;
    if (target.closest("[data-keep-open]")) return;
    close();
  };

  return (
    <span ref={wrapRef} className={cx("more", direction === "up" && "up", open && "on", className)}>
      {trigger({ open, toggle })}
      {open &&
        pos &&
        createPortal(
          <div className={cx("more on portal", className)}>
            <div ref={menuRef} className="menu" role="menu" aria-label={label} style={pos} onClick={onMenuClick}>
              {children}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}

/** One row in a More menu. `hint` prints quiet and right-aligned (`.k`). */
export function MenuItem({
  children,
  onSelect,
  hint,
  on,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  hint?: ReactNode;
  on?: boolean;
  disabled?: boolean;
}) {
  return (
    <button type="button" role="menuitem" className={on ? "on" : undefined} onClick={onSelect} disabled={disabled}>
      {children}
      {hint != null && <span className="k">{hint}</span>}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="sep" role="separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mlabel" role="presentation">
      {children}
    </div>
  );
}
